/**
 * Bar Color Providers Registry
 *
 * Defines all available bar coloring methods for price charts.
 * Each provider determines bar colors based on different criteria.
 */

import ema from "@chartLib/lib/calculator/ema";
import macd from "@chartLib/lib/calculator/macd";

/**
 * Cores padrão das barras — fonte ÚNICA do verde/vermelho histórico. Os métodos
 * de semântica alta/baixa (`supportsCustomColors`) recebem uma paleta resolvida
 * a partir daqui + o `barColorConfig` do gráfico (#112); os demais métodos têm
 * paleta própria com significado e não são sobrescritos.
 */
export const DEFAULT_BAR_COLORS = {
  upStroke: "#8cc176",
  downStroke: "#c53717",
  upFill: "#8cc176",
  downFill: "#c53717",
  wickStroke: "#687177",
};

/** Paleta → cores de UMA barra, conforme a direção. */
const paint = (isUp, palette) => ({
  fill: isUp ? palette.upFill : palette.downFill,
  stroke: isUp ? palette.upStroke : palette.downStroke,
  wickStroke: palette.wickStroke,
});

/**
 * Traditional bar coloring: Green if close > open, red otherwise
 */
export const traditionalProvider = {
  key: "traditional",
  displayName: "Tradicional (Abertura/Fechamento)",
  description: "Verde quando fechamento > abertura",
  category: "static",
  // Semântica alta/baixa → aceita as cores do usuário (#112).
  supportsCustomColors: true,

  getColors: (d, index, allData, indicatorConfigMap, palette) =>
    paint(d.close > d.open, palette || DEFAULT_BAR_COLORS),
};

/**
 * Previous close comparison: Green if close > previous close
 */
export const previousCloseProvider = {
  key: "previousClose",
  displayName: "Comparação com Fechamento Anterior",
  description: "Verde quando fechamento > fechamento anterior",
  category: "static",
  supportsCustomColors: true,

  getColors: (d, index, allData, indicatorConfigMap, palette) => {
    const p = palette || DEFAULT_BAR_COLORS;
    // First bar: use traditional logic
    if (index === 0) return paint(d.close > d.open, p);

    const previousBar = allData[index - 1];
    return paint(d.close > previousBar.close, p);
  },
};

/**
 * Volume comparison: Green if volume > previous volume
 */
export const volumeComparisonProvider = {
  key: "volumeComparison",
  displayName: "Volume Crescente/Decrescente",
  description: "Verde quando volume > volume anterior",
  category: "static",
  // Binário como os outros dois métodos básicos — só muda o CRITÉRIO (volume em
  // vez de preço). Mesmas duas cores, então respeita a paleta do usuário (#112).
  supportsCustomColors: true,

  getColors: (d, index, allData, indicatorConfigMap, palette) => {
    const p = palette || DEFAULT_BAR_COLORS;
    // First bar: use traditional logic
    if (index === 0) return paint(d.close > d.open, p);

    const previousBar = allData[index - 1];

    // `volume` is a flat number on the processed bar (e.g. 31183); older/other
    // shapes nested it as `{ volume }`. Support both so the comparison isn't
    // always-undefined (which painted every bar red).
    const currentVolume = d.volume?.volume ?? d.volume;
    const prevVolume = previousBar.volume?.volume ?? previousBar.volume;

    return paint(currentVolume > prevVolume, p);
  },
};

// Cache for Elder Impulse calculations (outside the provider to avoid re-render issues)
let elderImpulseCache = null;

/**
 * Elder Impulse bar coloring: green if both EMA and MACD rising, red if both falling, otherwise neutral
 * Uses calculators to compute EMA(13) and MACD(12,26,9) directly from price data
 */
export const elderImpulseProvider = {
  key: "indicator:elderImpulse",
  displayName: "Sistema de Impulso Elder",
  description: "Verde quando EMA e MACD sobem, vermelho quando ambos descem",
  category: "indicator",
  requiresIndicators: [], // No indicators required - we calculate directly

  getColors: (d, index, allData) => {
    // Need at least 2 bars to compare trends
    if (index === 0) {
      return {
        fill: "#999999", // Gray (neutral) for first bar
        stroke: "#999999",
        wickStroke: "#687177",
      };
    }

    // Calculate EMA and MACD for entire dataset (cached by data reference)
    // Only recalculate if data array reference changes
    if (!elderImpulseCache || elderImpulseCache.dataRef !== allData) {
      // Create EMA(13) calculator
      const emaCalculator = ema().options({
        windowSize: 13,
        sourcePath: "close",
      });

      // Create MACD(12, 26, 9) calculator
      const macdCalculator = macd().options({
        fast: 12,
        slow: 26,
        signal: 9,
        sourcePath: "close",
      });

      // Calculate EMA and MACD arrays
      const emaArray = emaCalculator(allData);
      const macdArray = macdCalculator(allData);

      // Cache results with data reference
      elderImpulseCache = {
        dataRef: allData,
        ema: emaArray,
        macd: macdArray,
      };
    }

    const { ema: emaArray, macd: macdArray } = elderImpulseCache;

    // Get current and previous values
    const emaValue = emaArray[index];
    const prevEmaValue = emaArray[index - 1];
    const macdValue = macdArray[index]?.divergence;
    const prevMacdValue = macdArray[index - 1]?.divergence;

    // If required data is missing, return neutral gray
    if (
      emaValue === undefined ||
      prevEmaValue === undefined ||
      macdValue === undefined ||
      prevMacdValue === undefined
    ) {
      return {
        fill: "#999999", // Gray
        stroke: "#999999",
        wickStroke: "#687177",
      };
    }

    // Calculate Elder Impulse logic (matching ChartIQ formula)
    // Uses strict comparison (>) to match ChartIQ behavior
    const emaRising = emaValue > prevEmaValue;
    const macdRising = macdValue > prevMacdValue;
    const emaFalling = emaValue < prevEmaValue;
    const macdFalling = macdValue < prevMacdValue;

    if (emaRising && macdRising) {
      // Both EMA and MACD histogram rising - bullish
      return {
        fill: "#00FF00", // Bright Green
        stroke: "#00FF00",
        wickStroke: "#687177",
      };
    } else if (emaFalling && macdFalling) {
      // Both EMA and MACD histogram falling - bearish
      return {
        fill: "#FF0000", // Bright Red
        stroke: "#FF0000",
        wickStroke: "#687177",
      };
    } else {
      // Neutral - conflicting signals or flat values
      return {
        fill: "#0099FF", // Blue (instead of gray)
        stroke: "#0099FF",
        wickStroke: "#687177",
      };
    }
  },
};

/**
 * RSI Overbought/Oversold bar coloring
 * Uses existing RSI indicator data from chart
 * Green when RSI < 30 (oversold), red when RSI > 70 (overbought), blue otherwise
 * Colors respect indicator's configured overbought/oversold appearance settings
 */
export const rsiColorProvider = {
  key: "indicator:rsi",
  displayName: "RSI Sobrecompra/Sobrevenda",
  description:
    "Verde quando RSI < 30 (sobrevenda), vermelho quando RSI > 70 (sobrecompra)",
  category: "indicator",
  requiresIndicators: ["rsi"], // Requires RSI indicator to be present

  getColors: (d, index, allData, indicatorConfigMap = {}) => {
    // Check if RSI data exists on this bar
    if (d.rsi === undefined || d.rsi === null) {
      // No RSI data - return neutral gray
      return {
        fill: "#999999",
        stroke: "#999999",
        wickStroke: "#687177",
      };
    }

    const rsiValue = d.rsi;

    // Extract configured colors from indicator appearance config
    const rsiConfig = indicatorConfigMap["rsi"];

    // Debug logging for first bar
    if (index === 0) {
      console.log("[RSI Color] indicatorConfigMap:", indicatorConfigMap);
      console.log("[RSI Color] rsiConfig:", rsiConfig);
      console.log("[RSI Color] appearanceConfig:", rsiConfig?.appearanceConfig);
      console.log(
        "[RSI Color] overBought:",
        rsiConfig?.appearanceConfig?.overBought,
      );
      console.log(
        "[RSI Color] overSold:",
        rsiConfig?.appearanceConfig?.overSold,
      );
    }

    // Extract threshold VALUES and colors from indicator config
    const overBoughtThreshold =
      rsiConfig?.appearanceConfig?.overBought?.value || 70;
    const overSoldThreshold =
      rsiConfig?.appearanceConfig?.overSold?.value || 30;
    const overBoughtColor =
      rsiConfig?.appearanceConfig?.overBought?.stroke?.color || "#FF5252";
    const overSoldColor =
      rsiConfig?.appearanceConfig?.overSold?.stroke?.color || "#00C853";
    const neutralColor =
      rsiConfig?.appearanceConfig?.middle?.stroke?.color || "#2196F3"; // Use middle/center line color

    // Debug: log colors being used
    if (index === 0) {
      console.log("[RSI Color] overBoughtThreshold:", overBoughtThreshold);
      console.log("[RSI Color] overSoldThreshold:", overSoldThreshold);
      console.log("[RSI Color] overBoughtColor:", overBoughtColor);
      console.log("[RSI Color] overSoldColor:", overSoldColor);
      console.log("[RSI Color] neutralColor (from middle):", neutralColor);
      console.log("[RSI Color] RSI value:", rsiValue);
    }

    // Oversold zone (RSI < threshold) - bullish signal
    if (rsiValue < overSoldThreshold) {
      return {
        fill: overSoldColor,
        stroke: overSoldColor,
        wickStroke: "#687177",
      };
    }

    // Overbought zone (RSI > threshold) - bearish signal
    if (rsiValue > overBoughtThreshold) {
      return {
        fill: overBoughtColor,
        stroke: overBoughtColor,
        wickStroke: "#687177",
      };
    }

    // Neutral zone (between thresholds) - use middle/center line color
    return {
      fill: neutralColor,
      stroke: neutralColor,
      wickStroke: "#687177",
    };
  },
};

/**
 * Stochastic %K/%D crossing bar coloring
 * Uses existing Stochastic indicator data from chart
 * Green when %K > %D (bullish), red when %K < %D (bearish)
 * Colors respect indicator's configured %K and %D line appearance settings
 */
export const stochasticColorProvider = {
  key: "indicator:stochastic",
  displayName: "Stochastic %K/%D",
  description: "Verde quando %K > %D (alta), vermelho quando %K < %D (baixa)",
  category: "indicator",
  requiresIndicators: ["stochastic"], // Requires Stochastic indicator

  getColors: (d, index, allData, indicatorConfigMap = {}) => {
    // Check if Stochastic data exists on this bar
    if (
      !d.stochastic ||
      d.stochastic.K === undefined ||
      d.stochastic.D === undefined
    ) {
      // No Stochastic data - return neutral gray
      return {
        fill: "#999999",
        stroke: "#999999",
        wickStroke: "#687177",
      };
    }

    const kValue = d.stochastic.K;
    const dValue = d.stochastic.D;

    // Extract configured colors from indicator appearance config
    const stochasticConfig = indicatorConfigMap["stochastic"];
    const kLineColor =
      stochasticConfig?.appearanceConfig?.kLine?.color || "#ff7f0e";
    const dLineColor =
      stochasticConfig?.appearanceConfig?.dLine?.color || "#17becf";
    const neutralColor = "#2196F3"; // Blue for neutral/equal

    // %K above %D - bullish signal (use %K color)
    if (kValue > dValue) {
      return {
        fill: kLineColor,
        stroke: kLineColor,
        wickStroke: "#687177",
      };
    }

    // %K below %D - bearish signal (use %D color)
    if (kValue < dValue) {
      return {
        fill: dLineColor,
        stroke: dLineColor,
        wickStroke: "#687177",
      };
    }

    // Equal values - neutral
    return {
      fill: neutralColor,
      stroke: neutralColor,
      wickStroke: "#687177",
    };
  },
};

/**
 * MACD Histogram bar coloring
 * Uses existing MACD indicator data from chart
 * Green when divergence > 0 (bullish), red when divergence < 0 (bearish)
 * Colors respect indicator's configured histogram appearance settings (barDivergence)
 */
export const macdColorProvider = {
  key: "indicator:macd",
  displayName: "MACD Histograma",
  description:
    "Verde quando histograma > 0 (alta), vermelho quando < 0 (baixa)",
  category: "indicator",
  requiresIndicators: ["macd"], // Requires MACD indicator

  getColors: (d, index, allData, indicatorConfigMap = {}) => {
    // Check if MACD data exists on this bar
    if (
      !d.macd ||
      d.macd.divergence === undefined ||
      d.macd.divergence === null
    ) {
      // No MACD data - return neutral gray
      return {
        fill: "#999999",
        stroke: "#999999",
        wickStroke: "#687177",
      };
    }

    const divergence = d.macd.divergence;

    // Extract configured colors from indicator appearance config
    const macdConfig = indicatorConfigMap["macd"];
    const increasingColor =
      macdConfig?.appearanceConfig?.barDivergence?.increasingColor || "#8cc176";
    const decreasingColor =
      macdConfig?.appearanceConfig?.barDivergence?.decreasingColor || "#b82c0c";
    const neutralColor = "#FFFF00"; // Yellow for zero/near-zero

    // Divergence positive - bullish signal (threshold for clarity)
    if (divergence > 0.001) {
      return {
        fill: increasingColor,
        stroke: increasingColor,
        wickStroke: "#687177",
      };
    }

    // Divergence negative - bearish signal (threshold for clarity)
    if (divergence < -0.001) {
      return {
        fill: decreasingColor,
        stroke: decreasingColor,
        wickStroke: "#687177",
      };
    }

    // Divergence at zero or very close - neutral
    return {
      fill: neutralColor,
      stroke: neutralColor,
      wickStroke: "#687177",
    };
  },
};

/**
 * All static bar color providers
 */
export const barColorProviders = {
  traditional: traditionalProvider,
  previousClose: previousCloseProvider,
  volumeComparison: volumeComparisonProvider,
};

/**
 * Get bar color provider by key
 */
export function getBarColorProvider(providerKey) {
  // Check static providers first
  if (barColorProviders[providerKey]) {
    return barColorProviders[providerKey];
  }

  // Check for indicator-based providers (format: "indicator:indicatorKey")
  if (providerKey?.startsWith("indicator:")) {
    const indicatorKey = providerKey.replace("indicator:", "");
    return getIndicatorBarColorProvider(indicatorKey);
  }

  // Fallback to traditional
  return barColorProviders.traditional;
}

/**
 * Get indicator-based bar color provider
 */
function getIndicatorBarColorProvider(indicatorKey) {
  // Check for Elder Impulse
  if (indicatorKey === "elderImpulse") {
    return elderImpulseProvider;
  }

  // Check for RSI
  if (indicatorKey === "rsi") {
    return rsiColorProvider;
  }

  // Check for Stochastic
  if (indicatorKey === "stochastic") {
    return stochasticColorProvider;
  }

  // Check for MACD
  if (indicatorKey === "macd") {
    return macdColorProvider;
  }

  // TODO: Scan indicator registry for more barColorProvider configurations
  console.warn(
    `[BarColorRegistry] Indicator-based provider not found: ${indicatorKey}`,
  );
  return barColorProviders.traditional;
}

/**
 * Get all available bar color providers (static + indicator-based)
 */
export function getAllBarColorProviders() {
  const providers = { ...barColorProviders };

  // TODO: Add indicator-based providers from registry scan
  // Will be implemented in Phase 3

  return providers;
}

/**
 * Check if a bar color provider is available based on active indicators
 * @param {Object} provider - The bar color provider to check
 * @param {Array} activeIndicators - Array of active indicators in the chart
 * @returns {boolean} - True if provider is available (all requirements met)
 */
function checkProviderAvailability(provider, activeIndicators = []) {
  // Static providers are always available
  if (provider.category === "static") {
    return true;
  }

  // If provider has no requirements, it's available
  if (
    !provider.requiresIndicators ||
    provider.requiresIndicators.length === 0
  ) {
    return true;
  }

  // Check if all required indicators are present in active indicators
  return provider.requiresIndicators.every((requiredType) =>
    activeIndicators.some((ind) => ind.type === requiredType),
  );
}

/**
 * Get grouped bar color providers for dropdown display
 * @param {Array} activeIndicators - Array of active indicators in the chart (optional)
 * @returns {Array} - Grouped providers with availability status
 */
export function getGroupedBarColorProviders(activeIndicators = []) {
  const staticProviders = Object.values(barColorProviders).map((provider) => ({
    ...provider,
    isAvailable: checkProviderAvailability(provider, activeIndicators),
  }));

  // Indicator-based providers
  const indicatorProviders = [
    elderImpulseProvider,
    rsiColorProvider,
    stochasticColorProvider,
    macdColorProvider,
    // Future indicator-based providers will be added here
  ].map((provider) => ({
    ...provider,
    isAvailable: checkProviderAvailability(provider, activeIndicators),
  }));

  return [
    {
      label: "Métodos Básicos",
      items: staticProviders,
    },
    {
      label: "Baseado em Indicadores",
      items: indicatorProviders,
    },
  ];
}
